import { z } from 'zod';

interface FieldDefinition {
  type?: string;
  name: string;
  required?: boolean;
   // eslint-disable-next-line @typescript-eslint/no-explicit-any
  default?: any;
  items?: {
    type: string;
    fields?: FieldDefinition[];
  };
  fields?: FieldDefinition[];
  options?: string[];
}

const createArraySchema = (field: FieldDefinition): z.ZodType => {
  if (field.items?.type === 'object' && Array.isArray(field.items.fields)) {
    const objectSchema = field.items.fields.reduce<Record<string, z.ZodType>>((acc, subField) => {
      const fieldSchema = generateFieldSchema(subField);
      if (fieldSchema) {
        acc[subField.name] = fieldSchema;
      }
      return acc;
    }, {});
    return z.array(z.object(objectSchema)).default([]);
  }
  return z.array(z.any()).default([]);
};

const createStringSchema = (field: FieldDefinition): z.ZodType => {
  return field.required 
    ? z.string().min(1) 
    : z.string().optional().default('');
};

const createNumberSchema = (field: FieldDefinition): z.ZodType => {
  return field.required 
    ? z.number() 
    : z.number().optional().default(0);
};

const createEnumSchema = (field: FieldDefinition): z.ZodType => {
  if (Array.isArray(field.options) && field.options.length > 0) {
    return z.enum(field.options as [string, ...string[]])
      .default(field.default || field.options[0]);
  }
  return z.string().default('');
};

const createObjectSchema = (field: FieldDefinition): z.ZodType => {
  if (Array.isArray(field.fields)) {
    const objectSchema = field.fields.reduce<Record<string, z.ZodType>>((acc, subField) => {
      acc[subField.name] = generateFieldSchema(subField);
      return acc;
    }, {});
    return z.object(objectSchema);
  }
  return z.record(z.any()).default({});
};

export const generateFieldSchema = (field: FieldDefinition): z.ZodType => {
  const fieldType = field.type?.toLowerCase();

  switch (fieldType) {
    case 'array':
      return createArraySchema(field);
    
    case 'string':
    case 'text':
    case 'file':
      return createStringSchema(field);
    
    case 'number':
    case 'integer':
      return createNumberSchema(field);
    
    case 'boolean':
      return z.boolean().default(false);
    
    case 'enum':
    case 'select':
      return createEnumSchema(field);
    
    case 'json':
      return z.string().optional().default('{}');
    
    case 'structured_filters':
      return z.any().optional().default([]);

    case 'attachments':
      return z.any().optional().default([]);

    case 'code':
      return createStringSchema(field);

    case 'object':
      return createObjectSchema(field);
    
    default:
      return z.string().default('');
  }
};
